import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  Stack,
  Chip,
  Divider,
  IconButton,
  CircularProgress,
  Grid,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Publish as PublishIcon,
  Unpublished as UnpublishIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import {
  fetchEvent,
  fetchEventCostsSummary,
  fetchEventRegistrations,
} from '../../slices/eventsSlice';
import CostManagement from './CostManagement';
import RegistrationManagement from './RegistrationManagement';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

const EventDetails = ({
  open,
  onClose,
  eventId,
  onEdit,
  onDelete,
  onPublish,
  onCancel,
}) => {
  const dispatch = useDispatch();
  const { currentEvent, costsSummary, registrations, loading } = useSelector((state) => state.events);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (open && eventId) {
      dispatch(fetchEvent(eventId));
      dispatch(fetchEventCostsSummary(eventId));
      dispatch(fetchEventRegistrations(eventId));
    }
  }, [open, eventId, dispatch]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (!currentEvent && loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (!currentEvent) {
    return null;
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  const formatTime = (timeString) => {
    if (!timeString) return '-';
    return timeString.substring(0, 5);
  };

  const EVENT_TYPE_LABELS = {
    assembly: 'اجتماع',
    holiday: 'عطلة',
    exam_period: 'فترة امتحانات',
    trip: 'رحلة',
    celebration: 'احتفال',
    meeting: 'اجتماع',
    workshop: 'ورشة عمل',
    sports_day: 'يوم رياضي',
    graduation: 'تخرج',
    other: 'أخرى',
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{currentEvent.title}</Typography>
          <Stack useFlexGap direction="row" spacing={1}>
            <IconButton size="small" onClick={() => onEdit(eventId)}>
              <EditIcon />
            </IconButton>
            {!currentEvent.is_cancelled && (
              <IconButton
                size="small"
                onClick={() => onPublish(eventId, !currentEvent.is_published)}
              >
                {currentEvent.is_published ? <UnpublishIcon /> : <PublishIcon />}
              </IconButton>
            )}
            {!currentEvent.is_cancelled && (
              <IconButton size="small" onClick={() => onCancel(eventId)}>
                <CancelIcon />
              </IconButton>
            )}
            <IconButton size="small" onClick={() => onDelete(eventId)} color="error">
              <DeleteIcon />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="نظرة عامة" />
          <Tab label="التكاليف" />
          <Tab label="التسجيلات" />
          <Tab label="الإحصائيات" />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                نوع الفعالية
              </Typography>
              <Chip
                label={EVENT_TYPE_LABELS[currentEvent.event_type] || currentEvent.event_type}
                color="primary"
                sx={{ mt: 0.5 }}
              />
            </Box>
            {currentEvent.description && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  الوصف
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {currentEvent.description}
                </Typography>
              </Box>
            )}
            <Divider />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  تاريخ البداية
                </Typography>
                <Typography variant="body1">{formatDate(currentEvent.start_date)}</Typography>
              </Grid>
              {currentEvent.end_date && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    تاريخ النهاية
                  </Typography>
                  <Typography variant="body1">{formatDate(currentEvent.end_date)}</Typography>
                </Grid>
              )}
              {currentEvent.start_time && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    وقت البداية
                  </Typography>
                  <Typography variant="body1">{formatTime(currentEvent.start_time)}</Typography>
                </Grid>
              )}
              {currentEvent.end_time && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    وقت النهاية
                  </Typography>
                  <Typography variant="body1">{formatTime(currentEvent.end_time)}</Typography>
                </Grid>
              )}
              {currentEvent.location && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    المكان
                  </Typography>
                  <Typography variant="body1">{currentEvent.location}</Typography>
                </Grid>
              )}
              {currentEvent.estimated_cost && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    التكلفة المقدرة
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {parseFloat(currentEvent.estimated_cost).toFixed(2)} ₪
                  </Typography>
                </Grid>
              )}
              {currentEvent.student_fee && parseFloat(currentEvent.student_fee) > 0 && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    رسوم الطالب
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'warning.dark' }}>
                    {parseFloat(currentEvent.student_fee).toFixed(2)} ₪ لكل طالب مسجَّل
                  </Typography>
                </Grid>
              )}
            </Grid>
            <Divider />
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                الحالة
              </Typography>
              <Stack useFlexGap direction="row" spacing={1} sx={{ mt: 0.5 }}>
                {currentEvent.is_cancelled ? (
                  <Chip label="ملغي" color="error" size="small" />
                ) : currentEvent.is_published ? (
                  <Chip label="منشور" color="success" size="small" />
                ) : (
                  <Chip label="غير منشور" color="default" size="small" />
                )}
                {currentEvent.requires_registration && (
                  <Chip label="يتطلب تسجيل" color="info" size="small" />
                )}
              </Stack>
            </Box>
          </Stack>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <CostManagement eventId={eventId} />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <RegistrationManagement eventId={eventId} event={currentEvent} />
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <Stack spacing={2}>
            {costsSummary && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  ملخص التكاليف
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        التكلفة المقدرة
                      </Typography>
                      <Typography variant="h6">
                        {costsSummary.total_estimated?.toFixed(2) || 0} ₪
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        التكلفة الفعلية
                      </Typography>
                      <Typography variant="h6">
                        {costsSummary.total_actual?.toFixed(2) || 0} ₪
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        المدفوع
                      </Typography>
                      <Typography variant="h6">
                        {costsSummary.total_paid?.toFixed(2) || 0} ₪
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        المتبقي
                      </Typography>
                      <Typography variant="h6">
                        {costsSummary.remaining?.toFixed(2) || 0} ₪
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                التسجيلات
              </Typography>
              <Typography variant="body1">
                إجمالي التسجيلات: {registrations.length || 0}
              </Typography>
            </Box>
          </Stack>
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>إغلاق</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventDetails;
